import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Field } from '../ui/Field';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { Campaign } from '../../lib/types';
import {
  PricyListKind,
  PricyPreviewResult,
  useCreatePricyCampaign,
  usePricyPreview,
} from '../../lib/api/pricy';

interface Props {
  open: boolean;
  onClose: () => void;
  campaign: Campaign;
}

/** Sets up the pricing campaign in Pricy (the PIM pricing tool) from the
 *  campaign's own data: pick the brand, the discount, optionally paste the
 *  product list from the brand's Excel, preview what it matches, create. */
export const PricyModal = ({ open, onClose, campaign }: Props) => {
  const toast = useToast();
  const brands = campaign.brand_details ?? [];
  const [brandId, setBrandId] = useState<string>('');
  const [discount, setDiscount] = useState('15');
  const [pricyStatus, setPricyStatus] = useState<'Active' | 'Draft'>('Active');
  const [listKind, setListKind] = useState<PricyListKind>('ean');
  const [productList, setProductList] = useState('');
  const [preview, setPreview] = useState<PricyPreviewResult | null>(null);

  const previewMutation = usePricyPreview(campaign.id);
  const createMutation = useCreatePricyCampaign(campaign.id);

  useEffect(() => {
    if (!open) return;
    setBrandId(brands.length === 1 ? brands[0].id : '');
    setDiscount('15');
    setPricyStatus('Active');
    setListKind('ean');
    setProductList('');
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const productCount = useMemo(
    () => productList.split(/[\n,]/).map((s) => s.trim()).filter(Boolean).length,
    [productList],
  );

  const missingDates = !campaign.start_date || !campaign.end_date;
  const discountNumber = parseFloat(discount);
  const discountValid = !Number.isNaN(discountNumber) && discountNumber > 0 && discountNumber <= 90;
  const canSubmit = !!brandId && discountValid && !missingDates && !createMutation.isPending;

  const errorDetail = (e: unknown): string => {
    const err = e as { response?: { data?: { detail?: string } } };
    return err.response?.data?.detail ?? 'Something went wrong talking to Pricy.';
  };

  const runPreview = () => {
    setPreview(null);
    previewMutation.mutate(
      { brand: brandId, product_list_kind: listKind, product_list: productList },
      {
        onSuccess: (data) => setPreview(data),
        onError: (e) => toast.push(errorDetail(e)),
      },
    );
  };

  const create = () => {
    createMutation.mutate(
      {
        brand: brandId,
        discount_value: discountNumber,
        pricy_status: pricyStatus,
        product_list_kind: listKind,
        product_list: productList,
      },
      {
        onSuccess: (data) => {
          toast.push(`Pricing campaign created in Pricy (#${data.pricy_campaign_id}).`);
          onClose();
        },
        onError: (e) => toast.push(errorDetail(e)),
      },
    );
  };

  const unmatched = preview?.unmatched_eans ?? preview?.unmatched_vendor_codes ?? [];

  return (
    <Modal open={open} onClose={onClose} title="Set up in Pricy" maxWidth={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
          Creates the pricing campaign in Pricy from this campaign&apos;s name and
          dates ({campaign.start_date ?? '—'} → {campaign.end_date ?? '—'}), on all
          markets, as a percentage discount.
        </div>

        {missingDates && (
          <div
            style={{
              background: 'var(--warn-bg)', color: 'var(--warn)',
              borderRadius: 'var(--r-md)', padding: '8px 10px', fontSize: 12.5,
            }}
          >
            This campaign has no start/end dates yet — set them first, Pricy needs both.
          </div>
        )}

        <Field label="Brand" required hint="One of the brands linked to this campaign. The Pricy scope targets its catalog.">
          <select className="input" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            <option value="">Pick a brand…</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
                {b.pim_vendor && b.pim_vendor !== b.name ? ` (PIM: ${b.pim_vendor})` : ''}
              </option>
            ))}
          </select>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Discount %" required hint="Percent off the current price (Relative).">
            <input
              className="input"
              type="number"
              min={1}
              max={90}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </Field>
          <Field label="Status in Pricy">
            <select
              className="input"
              value={pricyStatus}
              onChange={(e) => setPricyStatus(e.target.value as 'Active' | 'Draft')}
            >
              <option value="Active">Active (runs on the start date)</option>
              <option value="Draft">Draft</option>
            </select>
          </Field>
        </div>

        <Field
          label="Product list (optional)"
          hint="Paste one product per line (or comma-separated) from the brand's Excel. Leave empty to discount the brand's whole active catalog."
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            {(['ean', 'vendor_code'] as const).map((k) => (
              <button
                key={k}
                type="button"
                className="chip"
                onClick={() => { setListKind(k); setPreview(null); }}
                style={
                  listKind === k
                    ? { background: 'var(--ink)', color: '#fff', borderColor: 'var(--ink)' }
                    : {}
                }
              >
                {k === 'ean' ? 'EANs' : 'Vendor codes'}
              </button>
            ))}
          </div>
          <textarea
            className="input"
            rows={5}
            placeholder={listKind === 'ean' ? '3700677603166\n3700677603241\n…' : '201 T CONIC BL-BL\n201 T CONIC BL-BLCOP\n…'}
            value={productList}
            onChange={(e) => { setProductList(e.target.value); setPreview(null); }}
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 12 }}
          />
        </Field>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Button variant="ghost" onClick={runPreview} disabled={!brandId || previewMutation.isPending}>
            {previewMutation.isPending ? 'Checking…' : 'Check what it matches'}
          </Button>
          {productCount > 0 && (
            <span style={{ fontSize: 12, color: 'var(--ink-4)' }}>
              {productCount} {listKind === 'ean' ? 'EANs' : 'codes'} pasted
            </span>
          )}
        </div>

        {preview && (
          <div
            style={{
              background: 'var(--bg-subtle)', borderRadius: 'var(--r-md)',
              padding: '10px 12px', fontSize: 12.5,
            }}
          >
            <div>
              Matches <strong>{preview.product_number}</strong> products
              {' '}(<strong>{preview.product_variant_number}</strong> variants) for{' '}
              <strong>{preview.vendor}</strong> in the PIM catalog.
            </div>
            {preview.vendor_exists === false && (
              <div style={{ color: 'var(--warn)', marginTop: 4 }}>
                No products found for this vendor name — check the brand&apos;s
                &quot;PIM vendor&quot; spelling on its brand page.
              </div>
            )}
            {unmatched.length > 0 && (
              <div style={{ color: 'var(--warn)', marginTop: 4 }}>
                {unmatched.length} pasted {unmatched.length === 1 ? 'item' : 'items'} matched
                nothing active: {unmatched.slice(0, 5).join(', ')}
                {unmatched.length > 5 ? ` +${unmatched.length - 5} more` : ''}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={create} disabled={!canSubmit}>
            {createMutation.isPending ? 'Creating…' : 'Create in Pricy'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PricyModal;
